import type {
  AdmissionsAssistantWorkbookArtifact,
  AdmissionsAssistantWorkbookColumn,
  AdmissionsAssistantWorkbookInlinePreview,
  AdmissionsAssistantWorkbookInlinePreviewRow,
  AdmissionsAssistantWorkbookSummaryRow,
} from './types';

const DEFAULT_INLINE_PREVIEW_COLUMN_LIMIT = 6;
const DEFAULT_INLINE_PREVIEW_ROW_LIMIT = 8;

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const toText = (value: unknown): string | null => (
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
);

const toFiniteNonNegativeInteger = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? Math.floor(numeric) : null;
};

const normalizeColumn = (value: unknown, index: number): AdmissionsAssistantWorkbookColumn | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  const key = toText(record.key) ?? `column_${index + 1}`;
  const header = toText(record.header) ?? key;
  const kind = toText(record.kind);
  return {
    key,
    header,
    kind: kind === 'number'
      || kind === 'currency'
      || kind === 'percentage'
      || kind === 'date'
      ? kind
      : 'text',
    width: typeof record.width === 'number' && Number.isFinite(record.width)
      ? record.width
      : undefined,
  };
};

export const coerceAssistantWorkbookArtifact = (payload: unknown): AdmissionsAssistantWorkbookArtifact | null => {
  const root = asRecord(payload);
  if (!root) {
    return null;
  }
  const title = toText(root.title);
  const sheets = Array.isArray(root.sheets) ? root.sheets : [];
  if (!title || sheets.length === 0) {
    return null;
  }

  const normalizedSheets = sheets
    .map((sheet, sheetIndex) => {
      const sheetRecord = asRecord(sheet);
      if (!sheetRecord) {
        return null;
      }
      const name = toText(sheetRecord.name) ?? `Sheet ${sheetIndex + 1}`;
      const columns = (Array.isArray(sheetRecord.columns) ? sheetRecord.columns : [])
        .map((column, columnIndex) => normalizeColumn(column, columnIndex))
        .filter((column): column is AdmissionsAssistantWorkbookColumn => Boolean(column));
      if (columns.length === 0) {
        return null;
      }
      const rows = (Array.isArray(sheetRecord.rows) ? sheetRecord.rows : [])
        .map((row) => {
          const rowRecord = asRecord(row);
          if (!rowRecord) {
            return null;
          }
          const normalizedRow: Record<string, string | number | boolean | null> = {};
          columns.forEach((column) => {
            const value = rowRecord[column.key];
            normalizedRow[column.key] = (
              typeof value === 'string'
              || typeof value === 'number'
              || typeof value === 'boolean'
              || value == null
            )
              ? value as string | number | boolean | null
              : String(value);
          });
          return normalizedRow;
        })
        .filter((row): row is Record<string, string | number | boolean | null> => Boolean(row));
      const summaryRows = (Array.isArray(sheetRecord.summaryRows) ? sheetRecord.summaryRows : [])
        .map((row) => {
          const rowRecord = asRecord(row);
          if (!rowRecord) {
            return null;
          }
          const label = toText(rowRecord.label);
          if (!label) {
            return null;
          }
          const valuesRecord = asRecord(rowRecord.values);
          const values: Record<string, string | number | boolean | null> = {};
          if (valuesRecord) {
            columns.forEach((column) => {
              const value = valuesRecord[column.key];
              values[column.key] = (
                typeof value === 'string'
                || typeof value === 'number'
                || typeof value === 'boolean'
                || value == null
              )
                ? value as string | number | boolean | null
                : String(value);
            });
          }
          return {
            label,
            values,
          };
        })
        .filter((row): row is NonNullable<typeof row> => Boolean(row));

      return {
        id: toText(sheetRecord.id) ?? `sheet_${sheetIndex + 1}`,
        name,
        description: toText(sheetRecord.description) ?? undefined,
        tabColor: toText(sheetRecord.tabColor) ?? undefined,
        columns,
        rows,
        summaryRows: summaryRows.length > 0 ? summaryRows : undefined,
        cellFormats: (() => {
          const formatsRecord = asRecord(sheetRecord.cellFormats);
          if (!formatsRecord) {
            return undefined;
          }
          const entries: Array<[string, { backgroundColor?: string; textColor?: string; bold?: boolean }]> = [];
          Object.entries(formatsRecord).forEach(([key, value]) => {
              const formatRecord = asRecord(value);
              if (!formatRecord) {
                return;
              }
              entries.push([
                key,
                {
                  backgroundColor: toText(formatRecord.backgroundColor) ?? undefined,
                  textColor: toText(formatRecord.textColor) ?? undefined,
                  bold: formatRecord.bold === true ? true : undefined,
                },
              ]);
            });
          return entries.length > 0 ? Object.fromEntries(entries) : undefined;
        })(),
      };
    })
    .filter((sheet): sheet is NonNullable<typeof sheet> => Boolean(sheet));

  if (normalizedSheets.length === 0) {
    return null;
  }

  return {
    version: 1,
    title,
    description: toText(root.description) ?? undefined,
    generatedAt: toText(root.generatedAt) ?? undefined,
    sourceQuestion: toText(root.sourceQuestion) ?? undefined,
    appliedOperations: (Array.isArray(root.appliedOperations) ? root.appliedOperations : [])
      .map((entry) => toText(entry))
      .filter((entry): entry is string => Boolean(entry)),
    metadata: (() => {
      const metadataRecord = asRecord(root.metadata);
      if (!metadataRecord) {
        return undefined;
      }
      return {
        workspaceRootId: toText(metadataRecord.workspaceRootId) ?? undefined,
        versionId: toText(metadataRecord.versionId) ?? undefined,
        versionNumber: typeof metadataRecord.versionNumber === 'number' && Number.isFinite(metadataRecord.versionNumber)
          ? metadataRecord.versionNumber
          : undefined,
        previousVersionId: metadataRecord.previousVersionId == null
          ? null
          : toText(metadataRecord.previousVersionId) ?? null,
        manifestPath: metadataRecord.manifestPath == null
          ? null
          : toText(metadataRecord.manifestPath) ?? null,
        workbookArtifactPath: metadataRecord.workbookArtifactPath == null
          ? null
          : toText(metadataRecord.workbookArtifactPath) ?? null,
        xlsxArtifactPath: metadataRecord.xlsxArtifactPath == null
          ? null
          : toText(metadataRecord.xlsxArtifactPath) ?? null,
        csvArtifactPath: metadataRecord.csvArtifactPath == null
          ? null
          : toText(metadataRecord.csvArtifactPath) ?? null,
        updatedBy: metadataRecord.updatedBy === 'user' ? 'user' : metadataRecord.updatedBy === 'assistant' ? 'assistant' : undefined,
        updatedAt: toText(metadataRecord.updatedAt) ?? undefined,
      };
    })(),
    sheets: normalizedSheets,
  };
};

const normalizeWorkbookPreviewRow = (
  value: unknown,
  columns: AdmissionsAssistantWorkbookColumn[],
  index: number,
): AdmissionsAssistantWorkbookInlinePreviewRow | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  const valuesRecord = asRecord(record.values) ?? record;
  const values: Record<string, string | number | boolean | null> = {};
  columns.forEach((column) => {
    const cellValue = valuesRecord[column.key];
    values[column.key] = (
      typeof cellValue === 'string'
      || typeof cellValue === 'number'
      || typeof cellValue === 'boolean'
      || cellValue == null
    )
      ? cellValue as string | number | boolean | null
      : String(cellValue);
  });

  return {
    id: toText(record.id) ?? `row_${index + 1}`,
    values,
  };
};

const normalizeWorkbookPreviewSummaryRow = (
  value: unknown,
  columns: AdmissionsAssistantWorkbookColumn[],
): AdmissionsAssistantWorkbookSummaryRow | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  const label = toText(record.label);
  if (!label) {
    return null;
  }
  const valuesRecord = asRecord(record.values);
  const values: Record<string, string | number | boolean | null> = {};
  if (valuesRecord) {
    columns.forEach((column) => {
      const cellValue = valuesRecord[column.key];
      values[column.key] = (
        typeof cellValue === 'string'
        || typeof cellValue === 'number'
        || typeof cellValue === 'boolean'
        || cellValue == null
      )
        ? cellValue as string | number | boolean | null
        : String(cellValue);
    });
  }
  return {
    label,
    values,
  };
};

export const buildAssistantWorkbookInlinePreview = (
  workbook: AdmissionsAssistantWorkbookArtifact,
  options: {
    maxColumns?: number;
    maxRows?: number;
  } = {},
): AdmissionsAssistantWorkbookInlinePreview | null => {
  const activeSheet = workbook.sheets[0];
  if (!activeSheet || activeSheet.columns.length === 0) {
    return null;
  }

  const maxColumns = Math.max(1, Math.floor(options.maxColumns ?? DEFAULT_INLINE_PREVIEW_COLUMN_LIMIT));
  const maxRows = Math.max(1, Math.floor(options.maxRows ?? DEFAULT_INLINE_PREVIEW_ROW_LIMIT));
  const columns = activeSheet.columns.slice(0, maxColumns);
  const rows = activeSheet.rows.slice(0, maxRows).map((row, index) => {
    const values: Record<string, string | number | boolean | null> = {};
    columns.forEach((column) => {
      values[column.key] = row[column.key] ?? null;
    });
    return {
      id: `row_${index + 1}`,
      values,
    };
  });
  const summaryRows = activeSheet.summaryRows
    ?.slice(0, 2)
    .map((summaryRow) => {
      const values: Record<string, string | number | boolean | null> = {};
      if (summaryRow.values) {
        columns.forEach((column) => {
          values[column.key] = summaryRow.values?.[column.key] ?? null;
        });
      }
      return {
        label: summaryRow.label,
        values: summaryRow.values ? values : undefined,
      };
    });
  const omittedColumnCount = Math.max(0, activeSheet.columns.length - columns.length);
  const omittedRowCount = Math.max(0, activeSheet.rows.length - rows.length);

  return {
    title: workbook.title,
    description: workbook.description,
    activeSheetId: activeSheet.id,
    activeSheetName: activeSheet.name,
    sheetCount: workbook.sheets.length,
    totalRowCount: workbook.sheets.reduce((total, sheet) => total + sheet.rows.length, 0),
    activeSheetRowCount: activeSheet.rows.length,
    columns,
    rows,
    summaryRows: summaryRows && summaryRows.length > 0 ? summaryRows : undefined,
    truncation: {
      maxColumns,
      maxRows,
      omittedColumnCount,
      omittedRowCount,
      isColumnTruncated: omittedColumnCount > 0,
      isRowTruncated: omittedRowCount > 0,
    },
  };
};

export const coerceAssistantWorkbookInlinePreview = (payload: unknown): AdmissionsAssistantWorkbookInlinePreview | null => {
  const root = asRecord(payload);
  if (!root) {
    return null;
  }
  const title = toText(root.title);
  const activeSheetId = toText(root.activeSheetId);
  const activeSheetName = toText(root.activeSheetName);
  const columns = (Array.isArray(root.columns) ? root.columns : [])
    .map((column, columnIndex) => normalizeColumn(column, columnIndex))
    .filter((column): column is AdmissionsAssistantWorkbookColumn => Boolean(column));
  if (!title || !activeSheetId || !activeSheetName || columns.length === 0) {
    return null;
  }

  const rows = (Array.isArray(root.rows) ? root.rows : [])
    .map((row, rowIndex) => normalizeWorkbookPreviewRow(row, columns, rowIndex))
    .filter((row): row is AdmissionsAssistantWorkbookInlinePreviewRow => Boolean(row));
  const summaryRows = (Array.isArray(root.summaryRows) ? root.summaryRows : [])
    .map((row) => normalizeWorkbookPreviewSummaryRow(row, columns))
    .filter((row): row is AdmissionsAssistantWorkbookSummaryRow => Boolean(row));
  const truncationRecord = asRecord(root.truncation);
  const omittedColumnCount = toFiniteNonNegativeInteger(truncationRecord?.omittedColumnCount) ?? 0;
  const omittedRowCount = toFiniteNonNegativeInteger(truncationRecord?.omittedRowCount) ?? 0;
  const maxColumns = toFiniteNonNegativeInteger(truncationRecord?.maxColumns) ?? columns.length;
  const maxRows = toFiniteNonNegativeInteger(truncationRecord?.maxRows) ?? rows.length;
  const activeSheetRowCount = toFiniteNonNegativeInteger(root.activeSheetRowCount) ?? rows.length + omittedRowCount;

  return {
    title,
    description: toText(root.description) ?? undefined,
    activeSheetId,
    activeSheetName,
    sheetCount: toFiniteNonNegativeInteger(root.sheetCount) ?? 1,
    totalRowCount: toFiniteNonNegativeInteger(root.totalRowCount) ?? activeSheetRowCount,
    activeSheetRowCount,
    columns,
    rows,
    summaryRows: summaryRows.length > 0 ? summaryRows : undefined,
    truncation: {
      maxColumns,
      maxRows,
      omittedColumnCount,
      omittedRowCount,
      isColumnTruncated: truncationRecord?.isColumnTruncated === true || omittedColumnCount > 0,
      isRowTruncated: truncationRecord?.isRowTruncated === true || omittedRowCount > 0,
    },
  };
};

export const formatWorkbookCellValue = (
  column: AdmissionsAssistantWorkbookColumn,
  value: string | number | boolean | null | undefined,
): string => {
  if (value == null || value === '') {
    return '—';
  }
  if (column.kind === 'currency' && typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (column.kind === 'percentage' && typeof value === 'number') {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (column.kind === 'date') {
    const date = typeof value === 'string' || typeof value === 'number'
      ? new Date(value)
      : null;
    if (date && !Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
    }
  }
  if (typeof value === 'number') {
    return value.toLocaleString('en-US');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
};
